import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  BookOpen,
  Settings,
  MessageCircle,
  Mail,
  CheckCircle,
  Clock,
  XCircle,
  BarChart3,
  TrendingUp,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  Activity,
  Calendar
} from 'lucide-react';
import { useUser } from "@clerk/clerk-react";
import toast from 'react-hot-toast';
import ApiService from '../../services/ApiService';
import { BarChart, LineChart, PieChart, PiecewiseColorLegend } from '@mui/x-charts';
import DateRangePicker from '../ui/DatePicker';
import BarLabelAtBase from '../BarLabelAtBase';
import BarShadedBackground from '../BarShadedBackground';
import Stack from '@mui/material/Stack';
import ChartFooter from '../ui/ChartFooter';
import { DESCRIPTIONS } from '../../utils/descriptions';
import InfoTooltip from '../ui/InfoTooltip';
import { Paper, Typography } from '@mui/material';
import QueryPreviewModal from '../ui/QueryPreviewModal';
import {
  OverviewStatsSkeleton,
  OverviewChartSkeleton,
  OverviewLineChartSkeleton,
  OverviewBarChartSkeleton
} from '../ui/OverviewSkeletons';


// StatusCard component
const StatusCard = ({ icon: IconComponent, title, value, color = "blue", onClick }) => {
  const colorClasses = {
    blue: "bg-black",
    green: "bg-black",
    orange: "bg-black",
    gray: "bg-black"
  };

  return (
    <div
      className={`group relative bg-white overflow-hidden shadow-lg rounded-xl border border-gray-200 hover:shadow-xl hover:border-black transition-all duration-300 hover:-translate-y-1 ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="relative p-3 sm:p-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className={`flex-shrink-0 p-2 sm:p-2.5 ${colorClasses[color]} rounded-lg shadow-lg group-hover:scale-110 transition-transform duration-200`}>
            <IconComponent className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <dt className="text-xs font-medium text-gray-600 mb-0.5 truncate" title={title}>
              {title}
            </dt>
            <dd className="text-lg sm:text-xl font-bold text-gray-900">
              {value}
            </dd>
          </div>
        </div>
      </div>
    </div>
  );
};

// Empty State Component
const EmptyChart = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center justify-center h-48 sm:h-64 text-center px-4">
    <div className="p-3 sm:p-4 bg-gray-100 rounded-full mb-3 sm:mb-4">
      <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-black" />
    </div>
    <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{title}</h4>
    <p className="text-xs sm:text-sm text-gray-500 max-w-sm">{description}</p>
  </div>
);

// Sort Button Component
const SortButton = ({ sortOrder, onSort }) => {
  return (
    <button
      onClick={onSort}
      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-black bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
      title={`Sort ${sortOrder === 'desc' ? 'ascending' : 'descending'}`}
    >
      {sortOrder === 'desc' ? (
        <>
          <ArrowDown className="h-3.5 w-3.5" />
          <span>High to Low</span>
        </>
      ) : (
        <>
          <ArrowUp className="h-3.5 w-3.5" />
          <span>Low to High</span>
        </>
      )}
    </button>
  );
};

const getEmotionEmoji = (emotion) => {
  const emotionEmojis = {
    // Positive emotions
    'happy': '😊', 'excited': '🎉', 'joy': '😄', 'love': '❤️', 'satisfied': '😌',
    'grateful': '🙏', 'appreciation': '🙏', 'proud': '😎', 'relief': '😮‍💨',
    'calm': '😌', 'content': '☺️', 'enthusiastic': '🤩', 'optimistic': '😃',
    'hopeful': '🌟', 'amused': '😄', 'delighted': '😁',
    // Negative emotions
    'angry': '😠', 'frustrated': '😤', 'sad': '😢', 'disappointed': '😞',
    'upset': '😔', 'annoyed': '😒', 'irritated': '😑', 'furious': '🤬',
    'hurt': '💔', 'depressed': '😭', 'miserable': '😣', 'bitter': '😖',
    'resentful': '😾',
    // Anxiety/Fear related
    'anxious': '😰', 'anxiety': '😰', 'worried': '😟', 'nervous': '😬',
    'stressed': '😓', 'overwhelmed': '😵', 'fearful': '😨', 'scared': '😱',
    'panicked': '😰', 'tense': '😬',
    // Confusion/Uncertainty
    'confused': '😕', 'uncertain': '🤔', 'doubtful': '🤨', 'skeptical': '🤨',
    'puzzled': '😵‍💫', 'perplexed': '😵', 'bewildered': '😳',
    // Neutral
    'neutral': '😐', 'indifferent': '😶', 'bored': '😑', 'tired': '😴',
    // Surprise
    'surprised': '😲', 'shocked': '😱', 'amazed': '😮', 'astonished': '🤯',
    // Urgency/Action
    'urgent': '⚡', 'impatient': '⏰', 'rushed': '🏃',
    // Other
    'curious': '🤔', 'interested': '👀', 'disgusted': '🤢', 'embarrassed': '😳',
    'shame': '😳', 'guilty': '😔', 'lonely': '😔', 'jealous': '😒', 'envious': '😤'
  };
  const normalizedEmotion = emotion?.toLowerCase().trim();
  return emotionEmojis[normalizedEmotion] || '💭';
};

// Truncate URL for display
const truncateUrl = (url, maxLength = 40) => {
  if (!url || url.length <= maxLength) return url;

  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.replace('www.', '');
    const path = urlObj.pathname + urlObj.search;

    if (path.length > maxLength - domain.length - 5) {
      return `${domain}${path.substring(0, maxLength - domain.length - 8)}...`;
    }
    return `${domain}${path}`;
  } catch {
    return url.length > maxLength ? url.substring(0, maxLength - 3) + '...' : url;
  }
};

function Overview() {
  const { user } = useUser();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [lineChartData, setLineChartData] = useState([]);
  const [barChartData, setBarChartData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [hasFilter, setHasFilter] = useState(false);
  const [rootCause, setRootCause] = useState(null);
  const [queryClassification, setQueryClassification] = useState(null);
  const [sentimentData, setSentimentData] = useState(null);
  const [rootCauseSortOrder, setRootCauseSortOrder] = useState('desc');
  const [primaryCategorySortOrder, setPrimaryCategorySortOrder] = useState('desc');
  const [intentSortOrder, setIntentSortOrder] = useState('desc');
  const [emotionSortOrder, setEmotionSortOrder] = useState('desc');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalQueries, setModalQueries] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const [modalFilterType, setModalFilterType] = useState('');
  const [modalFilterValue, setModalFilterValue] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPagination, setModalPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    has_next: false,
    has_previous: false
  });
  const [takeaways, setTakeaways] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    loadOverview();
    loadAnalytics();
  }, []);

  // Custom Tooltip for Charts
  const CustomChartTooltip = (props) => {
    const { itemData, series } = props;
    if (!itemData) return null;

    let label = '';
    let value = '';
    let id = '';

    // Handle PieChart and BarChart
    if (series.data) {
      const dataItem = series.data[itemData.dataIndex];
      if (dataItem) {
        id = dataItem.id;
        // Handle label formatting - sometimes it has percentage/count appended
        label = dataItem.label ? dataItem.label.split('(')[0].split('|')[0].trim() : '';
        value = dataItem.value;
      }
    }

    let description = '';
    if (id) {
      if (DESCRIPTIONS.primary_category[id]) description = DESCRIPTIONS.primary_category[id];
      else if (DESCRIPTIONS.intent[id]) description = DESCRIPTIONS.intent[id];
      else if (DESCRIPTIONS.root_cause[id]) description = DESCRIPTIONS.root_cause[id];
      else if (DESCRIPTIONS.rag_performance?.[id]) description = DESCRIPTIONS.rag_performance[id];
      // Handle emotion separately if needed, but current DESCRIPTIONS structure doesn't seem to have 'emotion' key directly.
      // Assuming 'emotion' maps to 'sentiment' descriptions or similar if added later. 
      // For now, checking if descriptions exist for the ID in known categories.
    }

    if (!label && !value) return null;

    return (
      <Paper elevation={3} sx={{ p: 2, maxWidth: 300, bgcolor: 'rgba(255, 255, 255, 0.95)' }}>
        <Typography variant="subtitle2" color="primary" fontWeight="bold">
          {label}
        </Typography>
        <Typography variant="body2" sx={{ my: 1 }}>
          Count: <strong>{value}</strong>
        </Typography>
        {description && (
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, borderTop: 1, borderColor: 'divider', pt: 1, fontStyle: 'italic' }}>
            {description}
          </Typography>
        )}
      </Paper>
    );
  };

  // Sort function for chart data
  const sortChartData = (data, sortOrder) => {
    return [...data].sort((a, b) => {
      if (sortOrder === 'desc') {
        return b.value - a.value;
      }
      return a.value - b.value;
    });
  };

  const fetchModalQueries = async (page, filterType, filterValue) => {
    setModalLoading(true);

    try {
      // Build payload with CORRECT nested filter structure matching FilterSidebar
      const payload = {
        page: page,
        per_page: 10,
        filters: {}
      };

      // Add date range if active
      if (startDate && endDate) {
        payload.start_date = startDate.toISOString().split('T')[0];
        payload.end_date = endDate.toISOString().split('T')[0];
      }

      // Add specific filter with NESTED structure matching FilterSidebar
      if (filterType === 'root_cause_category') {
        payload.filters.root_cause = {
          category: [filterValue]
        };
      } else if (filterType === 'emotion') {
        payload.filters.sentiment = {
          emotion: [filterValue]
        };
      } else if (filterType === 'primary_category') {
        payload.filters.classification = {
          primary_category: [filterValue]
        };
      } else if (filterType === 'intent') {
        payload.filters.classification = {
          intent: [filterValue]
        };
      }

      console.log('📊 Fetching queries with payload:', JSON.stringify(payload, null, 2));

      // Fetch filtered queries
      const response = await ApiService.getSupportQueries(payload);

      console.log('✅ API Response:', response);

      // Handle different response structures
      let queriesData = [];

      if (response.success && response.data) {
        queriesData = response.data;
      } else if (Array.isArray(response)) {
        queriesData = response;
      } else if (response.queries) {
        queriesData = response.queries;
      }

      if (response.pagination) {
        setModalPagination(response.pagination);
      }

      console.log(`📝 Found ${queriesData.length} queries`);
      if (queriesData.length > 0) {
        console.log('🔍 First query sample:', queriesData[0]);
      }

      if (queriesData && queriesData.length > 0) {
        setModalQueries(queriesData);
      } else {
        setModalQueries([]);
        if (page === 1) toast.info('No queries found for this filter');
      }
    } catch (error) {
      console.error('❌ Failed to fetch filtered queries:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.message || 'Failed to load queries');
      setModalQueries([]);
    } finally {
      setModalLoading(false);
    }
  };

  const handleModalPageChange = (newPage) => {
    fetchModalQueries(newPage, modalFilterType, modalFilterValue);
  };

  // Handle chart segment click - FIXED WITH CORRECT FILTER STRUCTURE
  const handleChartClick = async (filterType, filterValue, title) => {
    setModalOpen(true);
    setModalTitle(title);
    setModalFilterType(filterType);
    setModalFilterValue(filterValue);
    setModalQueries([]);

    // Fetch initial page
    await fetchModalQueries(1, filterType, filterValue);
  };

  const loadOverview = async (params = {}) => {
    try {
      if (Object.keys(params).length === 0) {
        setLoading(true);
      } else {
        setChartLoading(true);
      }
      const data = await ApiService.getDashboardOverview(params);
      const response = await ApiService.rootCause(params);
      const responseClassification = await ApiService.queryClassification(params);
      const responseSentiment = await ApiService.sentiment(params);
      setOverview(data);

      if (responseSentiment.success && responseSentiment.analytics) {
        const { emotion_distribution } = responseSentiment.analytics;
        const emotionData = emotion_distribution?.map(d => ({
          id: d.emotion,
          label: `${getEmotionEmoji(d.emotion)} ${d.emotion.charAt(0).toUpperCase() + d.emotion.slice(1)}`,
          value: d.count,
          emotion: d.emotion
        })) || [];
        setSentimentData({ emotion: emotionData });
      }

      if (responseClassification.success && responseClassification.analytics) {
        const { primary_category_distribution, intent_distribution } = responseClassification.analytics;

        const totalPrimaryQueries = primary_category_distribution?.reduce((sum, item) => sum + item.count, 0) || 0;
        const totalIntentQueries = intent_distribution?.reduce((sum, item) => sum + item.count, 0) || 0;

        const primaryCategoryData = primary_category_distribution?.map(item => ({
          id: item.primary_category,
          label: item.primary_category.replace(/_/g, ' ').toUpperCase(),
          value: item.count,
          percentage: ((item.count / totalPrimaryQueries) * 100).toFixed(1)
        })) || [];

        const intentData = intent_distribution?.map(item => ({
          id: item.intent,
          label: item.intent.replace(/_/g, ' ').toUpperCase(),
          value: item.count,
          percentage: ((item.count / totalIntentQueries) * 100).toFixed(1)
        })) || [];

        const queryClassification = {
          primaryCategory: primaryCategoryData,
          intent: intentData,
          totalQueries: totalPrimaryQueries,
          topCategory: primary_category_distribution?.[0]?.primary_category.replace(/_/g, ' ').toUpperCase() || 'N/A',
          topIntent: intent_distribution?.[0]?.intent.replace(/_/g, ' ').toUpperCase() || 'N/A',
          dateRange: responseClassification.analytics.date_range
        }
        setQueryClassification(queryClassification)
      }

      if (response.success && response.analytics) {
        const { category_distribution, root_cause_identification_avg_confidence } = response.analytics;

        const totalRootCauseQueries = category_distribution?.reduce((sum, item) => sum + item.count, 0) || 0;

        const categoryData = category_distribution?.map(item => ({
          id: item.root_cause_category,
          label: item.root_cause_category.replace(/_/g, ' ').toUpperCase(),
          value: item.count,
          percentage: ((item.count / totalRootCauseQueries) * 100).toFixed(1)
        })) || [];

        const rootCause = {
          category: categoryData,
          avgConfidence: Math.round(root_cause_identification_avg_confidence * 100) || 0,
          totalCauses: category_distribution?.length || 0,
          totalQueries: totalRootCauseQueries,
          topCause: category_distribution?.[0]?.root_cause_category.replace(/_/g, ' ').toUpperCase() || 'N/A',
          topCausePercentage: category_distribution?.length > 0
            ? Math.round((category_distribution[0].count / totalRootCauseQueries) * 100)
            : 0,
          dateRange: response.analytics.date_range
        }
        setRootCause(rootCause);

        // Generate Takeaways
        generateOverviewTakeaways(
          data,
          rootCause,
          queryClassification || null, // Might be null if async timing varies, but ideally passed or state used
          sentimentData || null
        );
        // Note: queryClassification and sentimentData states might not be updated immediately in this closure.
        // Better to pass the raw response objects or local variables if available.
        // Re-deriving local variables for safe passing:

        let localQueryClassification = null;
        if (responseClassification.success && responseClassification.analytics) {
          const { primary_category_distribution } = responseClassification.analytics;
          localQueryClassification = {
            topCategory: primary_category_distribution?.[0]?.primary_category.replace(/_/g, ' ').toUpperCase() || 'N/A'
          };
        }

        let localSentiment = null;
        if (responseSentiment.success && responseSentiment.analytics) {
          const { emotion_distribution } = responseSentiment.analytics;
          if (emotion_distribution && emotion_distribution.length > 0) {
            localSentiment = {
              emotion: [{ emotion: emotion_distribution[0].emotion }]
            };
          }
        }

        const finalTakeaways = generateOverviewTakeaways(data, rootCause, localQueryClassification, localSentiment);
        setTakeaways(finalTakeaways);

      }
    } catch (error) {
      console.error('Failed to load overview:', error);
      toast.error('Failed to load dashboard overview');
    } finally {
      setLoading(false);
      setChartLoading(false);
    }
  };

  const loadAnalytics = async (params = {}) => {
    try {
      if (Object.keys(params).length === 0) {
        setLoading(true);
      } else {
        setChartLoading(true);
      }
      const data = await ApiService.getDashboardAnalytics(params, startDate && endDate);

      // Reset all chart data first
      setLineChartData([]);
      setBarChartData([]);
      setPieChartData([]);

      if (data?.analytics?.daily_traffic?.length > 0) {
        setLineChartData(data.analytics.daily_traffic);
      }

      if (data?.analytics?.status_distribution && Object.keys(data.analytics.status_distribution).length > 0) {
        const pie = Object.keys(data.analytics.status_distribution).map((value, index) => ({
          id: index + 1,
          label: value,
          value: data.analytics.status_distribution[value]
        }));
        setPieChartData(pie);
      }

      if (data?.analytics?.top_sources?.length > 0) {
        const formattedData = data.analytics.top_sources.map((item) => ({
          source: item?.source || 'Unknown',
          count: item?.count || 0,
          shortSource: truncateUrl(item?.source || 'Unknown', 35)
        }));
        setBarChartData(formattedData);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load dashboard analytics');
    } finally {
      setLoading(false);
      setChartLoading(false);
    }
  };

  const handleStatClick = (href) => {
    navigate(href);
  };

  const handleDateRangeChange = async (since, until) => {
    if (!since || !until) {
      setStartDate(null);
      setEndDate(null);
      setHasFilter(false);
      await Promise.all([loadOverview(), loadAnalytics()]);
      toast.success('Filter cleared');
      return;
    }

    setStartDate(since);
    setEndDate(until);
    setHasFilter(true);

    // Add one day to include the entire end date for API compatibility
    const endDateObj = dayjs(until).add(1, 'day');

    const params = {
      start_date: dayjs(since).format('YYYY-MM-DD'),
      end_date: endDateObj.format('YYYY-MM-DD')
    };

    await Promise.all([loadOverview(params), loadAnalytics(params)]);
  };

  const handleClearFilter = async () => {
    setStartDate(null);
    setEndDate(null);
    setHasFilter(false);
    localStorage.removeItem('defaultDateRange');
    await Promise.all([loadOverview(), loadAnalytics()]);
  };

  const generateUniqueColors = (count) => {
    const colors = [];
    const usedColors = new Set();

    while (colors.length < count) {
      const color = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
      if (!usedColors.has(color)) {
        colors.push(color);
        usedColors.add(color);
      }
    }

    return colors;
  };

  const generateOverviewTakeaways = (overviewData, rootCauseData, classificationData, sentiment) => {
    const insights = [];

    // 1. Overall Health / Query Volume
    if (overviewData) {
      const { total_queries, pending_queries } = overviewData;
      if (pending_queries > 0) {
        const pendingPercentage = Math.round((pending_queries / total_queries) * 100);
        if (pendingPercentage > 20) {
          insights.push({
            type: 'action',
            text: `Attention: **${pendingPercentage}%** of queries are still pending. Resource allocation may be needed.`
          });
        } else {
          insights.push({
            type: 'positive',
            text: `Healthy response rate with only **${pendingPercentage}%** pending queries.`
          });
        }
      }
    }

    // 2. Root Cause Analysis
    if (rootCauseData && rootCauseData.topCause) {
      insights.push({
        type: 'neutral',
        text: `The primary driver for user queries is **${rootCauseData.topCause}**, accounting for **${rootCauseData.topCausePercentage}%** of volume.`
      });
    }

    // 3. Query Classification
    if (classificationData && classificationData.topCategory) {
      insights.push({
        type: 'neutral',
        text: `Most users are engaging with **${classificationData.topCategory}** topics.`
      });
    }

    // 4. Sentiment
    if (sentiment && sentiment.emotion && sentiment.emotion.length > 0) {
      const topEmotion = sentiment.emotion[0];
      insights.push({
        type: 'sentiment',
        text: `The dominant user emotion is **${topEmotion.emotion.toUpperCase()}**.`
      });
    }

    return insights;
  };

  // Get sorted data based on sort order
  const getSortedRootCauseData = () => {
    if (!rootCause?.category) return [];
    return sortChartData(rootCause.category, rootCauseSortOrder);
  };

  const getSortedPrimaryCategoryData = () => {
    if (!queryClassification?.primaryCategory) return [];
    return sortChartData(queryClassification.primaryCategory, primaryCategorySortOrder);
  };

  const getSortedIntentData = () => {
    if (!queryClassification?.intent) return [];
    return sortChartData(queryClassification.intent, intentSortOrder);
  };

  const getSortedEmotionData = () => {
    if (!sentimentData?.emotion) return [];
    return sortChartData(sentimentData.emotion, emotionSortOrder);
  };

  if (loading && !overview) {
    return (
      <div className="space-y-4 sm:space-y-5 overview">
        {/* Header Skeleton */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="w-full sm:w-auto">
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <div className="h-10 w-full sm:w-48 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Welcome Section Skeleton */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
          <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
        </div>

        {/* Stats Grid Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {[1, 2, 3].map((i) => (
            <OverviewStatsSkeleton key={i} />
          ))}
        </div>

        {/* Query Status Skeleton */}
        <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-4 sm:p-5">
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse mb-4"></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <OverviewStatsSkeleton key={i} />
            ))}
          </div>
          <div className="mt-5">
            <div className="h-4 w-full bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-3 w-full bg-gray-200 rounded-full animate-pulse"></div>
          </div>
        </div>

        {/* Charts Grid Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
          <OverviewLineChartSkeleton />
          <OverviewChartSkeleton />
        </div>

        {/* Bottom Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 mt-4">
          <OverviewChartSkeleton />
          <OverviewChartSkeleton />
        </div>

        <OverviewBarChartSkeleton />
        <OverviewBarChartSkeleton />
        <OverviewBarChartSkeleton />
      </div>
    );
  }

  const stats = [
    {
      name: 'Content Items',
      value: overview?.content_count || 0,
      icon: BookOpen,
      href: '/knowledge',
    },
    {
      name: 'FAQs Generated',
      value: overview?.faq_count || 0,
      icon: MessageCircle,
      href: '/optimization',
    },
    {
      name: 'Active Widgets',
      value: overview?.widget_count || 0,
      icon: Settings,
      href: '/widgets',
    },
  ];

  const sortedRootCauseData = getSortedRootCauseData();
  const sortedPrimaryCategoryData = getSortedPrimaryCategoryData();
  const sortedIntentData = getSortedIntentData();
  const sortedEmotionData = getSortedEmotionData();

  return (
    <div className="space-y-3 overview">
      {/* Header with Date Picker */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent leading-tight">Dashboard Overview</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Monitor your AI-powered engagement platform.</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto z-10">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateRangeChange}
            className="flex-1 sm:flex-initial"
          />

          {hasFilter && (
            <button
              onClick={handleClearFilter}
              className="px-3 py-2 text-xs sm:text-sm font-medium text-black bg-white border-2 border-gray-200 rounded-lg hover:border-black hover:shadow-md transition-all duration-200"
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>

      {/* Welcome Card & Stats Grid */}
      <div className="grid grid-cols-1 gap-2">
        {/* Welcome Section */}
        <div className="lg:col-span-2 bg-black rounded-2xl p-4 sm:p-5 text-white shadow-xl relative overflow-hidden group">
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl group-hover:bg-white/10 transition-colors duration-500" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24 blur-2xl group-hover:bg-white/10 transition-colors duration-500" />

          <div className="relative flex flex-col h-full justify-between">
            {/* <div> */}
              {/* <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <div className="p-1.5 bg-white/10 rounded-lg backdrop-blur-sm">
                  <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-white/80 tracking-wide uppercase">
                  System Status
                </span>
              </div> */}
              <h2 className="text-xl sm:text-2xl font-bold mb-0.5">
                Welcome to Uttik
              </h2>
              <p className="text-sm sm:text-base text-white/70 leading-relaxed">
                Your AI-powered customer engagement solution is ready to help you provide exceptional support.
              </p>
            {/* </div> */}
          </div>
        </div>

      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {stats.map((stat) => (
          <div
            key={stat.name}
            onClick={() => handleStatClick(stat.href)}
            className="bg-white rounded-2xl p-2.5 border border-gray-100 shadow-sm hover:shadow-md hover:border-black transition-all duration-300 group cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <div className="p-1.5 sm:p-2 bg-gray-50 rounded-xl group-hover:bg-black group-hover:scale-110 transition-all duration-300">
                <stat.icon className="h-4 w-4 sm:h-5 sm:w-5 text-black group-hover:text-white" />
              </div>
              <div className="text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider mt-0.5 sm:mt-1">
                {stat.name}
              </div>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        {/* Support Query Status */}
        <div className="bg-white rounded-2xl p-3.5 sm:p-4.5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Activity className="h-5 w-5 text-black" />
              Support Query Status
            </h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
            <StatusCard
              title="Total Queries"
              value={overview?.total_queries || 0}
              icon={Mail}
              color="blue"
              onClick={() => navigate('/support?status=all')}
            />
            <StatusCard
              title="Acknowledged"
              value={overview?.total_queries || 0}
              icon={CheckCircle}
              color="blue"
              onClick={() => navigate('/support?status=acknowledged')}
            />
            <StatusCard
              title="Follow-up"
              value={overview?.responded_queries || 0}
              icon={MessageCircle}
              color="green"
              onClick={() => navigate('/support?status=responded')}
            />
            <StatusCard
              title="Pending Follow-up"
              value={overview?.pending_queries || 0}
              icon={Clock}
              color="orange"
              onClick={() => navigate('/support?status=pending')}
            />
            <StatusCard
              title="Closed"
              value={overview?.closed_queries || 0}
              icon={XCircle}
              color="gray"
              onClick={() => navigate('/support?status=closed')}
            />
          </div>

          {/* Progress Bar */}
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
              <span className="font-medium">Query Distribution</span>
              <span className="font-semibold">{overview?.total_queries || 0} Total</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
              <div className="h-full flex">
                <div
                  className="bg-black h-full transition-all duration-500"
                  style={{
                    width: overview?.total_queries > 0
                      ? `${((overview?.responded_queries || 0) / overview.total_queries) * 100}%`
                      : '0%'
                  }}
                ></div>
                <div
                  className="bg-gray-500 h-full transition-all duration-500"
                  style={{
                    width: overview?.total_queries > 0
                      ? `${((overview?.pending_queries || 0) / overview.total_queries) * 100}%`
                      : '0%'
                  }}
                ></div>
                <div
                  className="bg-gray-300 h-full transition-all duration-500"
                  style={{
                    width: overview?.total_queries > 0
                      ? `${((overview?.closed_queries || 0) / overview.total_queries) * 100}%`
                      : '0%'
                  }}
                ></div>
              </div>

            </div>

            <div className="flex items-center justify-center flex-wrap gap-3 sm:gap-4 mt-4 text-xs">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-black rounded-full mr-2"></div>
                <span className="text-gray-700 font-medium">Follow-up ({overview?.responded_queries || 0})</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-gray-500 rounded-full mr-2"></div>
                <span className="text-gray-700 font-medium">Pending Follow-up ({overview?.pending_queries || 0})</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-gray-300 rounded-full mr-2"></div>
                <span className="text-gray-700 font-medium">Closed ({overview?.closed_queries || 0})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section with Loading State */}
      <div className="relative">
        {chartLoading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 sm:h-16 w-12 sm:w-16 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p className="text-xs sm:text-sm font-medium text-gray-700">Loading data...</p>
            </div>
          </div>
        )}
        {/* Key Takeaways Section */}
        {takeaways.length > 0 && (
          <div className="bg-black rounded-xl shadow-lg p-6 text-white border border-black mb-4">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-white" />
              Key Takeaways
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {takeaways.map((takeaway, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 flex items-start gap-3">
                  <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${takeaway.type === 'action' ? 'bg-gray-500' :
                    takeaway.type === 'positive' ? 'bg-white' :
                      takeaway.type === 'sentiment' ? 'bg-gray-300' : 'bg-gray-400'
                    }`} />
                  <p className="text-sm leading-relaxed">
                    <span dangerouslySetInnerHTML={{
                      __html: takeaway.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    }} />
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
          {/* Line Chart */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-5 border border-gray-200">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">Daily Query Trend</h3>
            {lineChartData.length > 0 ? (
              <div className="w-full overflow-x-auto">
                <LineChart
                  xAxis={[{
                    data: lineChartData.map(item => new Date(item.date)),
                    scaleType: 'time',
                    valueFormatter: (date) =>
                      new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  }]}
                  series={[{
                    data: lineChartData.map(item => item.count),
                    baseline: 'min',
                    label: 'Daily Query',
                    color: '#000000',
                  }]}
                  height={250}
                />
              </div>
            ) : (
              <EmptyChart
                icon={TrendingUp}
                title="No Query Data"
                description="Query data will appear here once there is activity on your platform."
              />
            )}
          </div>

          {/* Pie Chart */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-5 border border-gray-200">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">Status Distribution</h3>
            {pieChartData.length > 0 ? (
              <div className="flex justify-center w-full overflow-x-auto">
                <PieChart
                  series={[{
                    data: pieChartData,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                    faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                  }]}
                  width={380}
                  height={220}
                />
              </div>
            ) : (
              <EmptyChart
                icon={BarChart3}
                title="No Status Data"
                description="Status distribution will be displayed once queries are created."
              />
            )}
          </div>
        </div>

        {/* Root Cause and Primary Category Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 mt-4">
          {/* Root Cause Category Distribution */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-5 border border-gray-200">
            <div className="flex justify-between items-center mb-3">
              <div>
                <div className="flex items-center">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900">Root Cause Distribution</h3>
                  <InfoTooltip text="Underlying reasons for the user's issue or inquiry." />
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Total: {rootCause?.totalQueries || 0} queries • Click to filter
                </p>
              </div>
              <SortButton
                sortOrder={rootCauseSortOrder}
                onSort={() => setRootCauseSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              />
            </div>
            {sortedRootCauseData.length > 0 ? (
              <div className="w-full overflow-x-auto">
                <PieChart
                  series={[{
                    data: sortedRootCauseData.map(item => ({
                      ...item,
                      label: `${item.label} (${item.value} - ${item.percentage}%)`
                    })),
                    highlightScope: { faded: 'global', highlighted: 'item' },
                  }]}
                  colors={generateUniqueColors(sortedRootCauseData.length)}
                  height={220}
                  tooltip={{ trigger: 'item' }}
                  slots={{ itemContent: CustomChartTooltip }}
                  onItemClick={(event, d) => {
                    const clickedItem = sortedRootCauseData[d.dataIndex];
                    handleChartClick(
                      'root_cause_category',
                      clickedItem.id,
                      `Root Cause: ${clickedItem.label}`
                    );
                  }}
                  sx={{
                    cursor: 'pointer',
                    '& .MuiPieArc-root': {
                      cursor: 'pointer',
                    },
                  }}
                />
                {/* Summary Stats - CLICKABLE */}
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {sortedRootCauseData.slice(0, 4).map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => handleChartClick('root_cause_category', item.id, `Root Cause: ${item.label}`)}
                      >
                        <span className="text-gray-700 truncate">{item.label.split(' (')[0]}</span>
                        <span className="font-semibold text-gray-900 ml-2">{item.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <EmptyChart
                icon={TrendingUp}
                title="No Root Cause Data"
                description="Root cause analysis will appear here once there is activity on your platform."
              />
            )}
            <ChartFooter dateRange={rootCause?.dateRange} />
          </div>

          {/* Primary Category Distribution */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-5 border border-gray-200">
            <div className="flex justify-between items-center mb-3">
              <div>
                <div className="flex items-center">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900">Primary Category Distribution</h3>
                  <InfoTooltip text="Distribution of queries based on their primary topic or domain." />
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Total: {queryClassification?.totalQueries || 0} queries • Click to filter
                </p>
              </div>
              <SortButton
                sortOrder={primaryCategorySortOrder}
                onSort={() => setPrimaryCategorySortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              />
            </div>
            {sortedPrimaryCategoryData.length > 0 ? (
              <div className="flex justify-center w-full overflow-x-auto">
                <div className="w-full">
                  <PieChart
                    series={[{
                      data: sortedPrimaryCategoryData.map(item => ({
                        ...item,
                        id: item.id, // Make sure id is included
                        label: `${item.label} (${item.value} - ${item.percentage}%)`,
                      })),
                      highlightScope: { faded: 'global', highlighted: 'item' },
                    }]}
                    colors={generateUniqueColors(sortedPrimaryCategoryData.length)}
                    height={220}
                    tooltip={{ trigger: 'item' }}
                    slots={{ itemContent: CustomChartTooltip }}
                    onItemClick={(event, d) => {
                      const clickedItem = sortedPrimaryCategoryData[d.dataIndex];
                      handleChartClick(
                        'primary_category',
                        clickedItem.id,
                        `Primary Category: ${clickedItem.label}`
                      );
                    }}
                    sx={{
                      cursor: 'pointer',
                      '& .MuiPieArc-root': {
                        cursor: 'pointer',
                      },
                    }}
                  />
                  {/* Summary Stats - CLICKABLE */}
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      {sortedPrimaryCategoryData.slice(0, 4).map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer transition-colors"
                          onClick={() => handleChartClick('primary_category', item.id, `Primary Category: ${item.label}`)}
                        >
                          <span className="text-gray-700 truncate">{item.label.split(' (')[0]}</span>
                          <span className="font-semibold text-gray-900 ml-2">{item.percentage}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <EmptyChart
                icon={BarChart3}
                title="No Category Data"
                description="Category distribution will be displayed once queries are created."
              />
            )}
            <ChartFooter dateRange={queryClassification?.dateRange} />
          </div>
        </div>


        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 mt-4">
          {/* Sentiment Analysis Chart - Full Width */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-5 border border-gray-200 mt-4">
            <div className="flex justify-between items-center mb-6">
              <div>
                <div className="flex items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Emotion Analysis</h3>
                  <InfoTooltip text="Distribution of emotions detected in user queries." />
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Total: {sortedEmotionData.reduce((sum, item) => sum + item.value, 0)} emotions • Click to filter
                </p>
              </div>
              <SortButton sortOrder={emotionSortOrder} onSort={() => setEmotionSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')} />
            </div>
            {sortedEmotionData.length > 0 ? (
              <div className="space-y-4">
                {/* Summary stats */}
                {/* <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                            <p className="text-xs font-medium text-green-600 mb-1">Total Emotions</p>
                            <p className="text-2xl font-bold text-green-900">
                                {sortedEmotionData.reduce((sum, d) => sum + d.value, 0)}
                            </p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                            <p className="text-xs font-medium text-blue-600 mb-1">Most Common</p>
                            <p className="text-lg font-bold text-blue-900 flex items-center gap-2">
                                {getEmotionEmoji(sortedEmotionData[0]?.emotion)}
                                <span className="capitalize">{sortedEmotionData[0]?.emotion}</span>
                            </p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                            <p className="text-xs font-medium text-purple-600 mb-1">Categories</p>
                            <p className="text-2xl font-bold text-purple-900">
                                {sortedEmotionData.length}
                            </p>
                        </div>
                    </div> */}

                <Stack>
                  <BarChart
                    height={400}
                    dataset={sortedEmotionData}
                    series={[{
                      id: 'turnout',
                      dataKey: 'value',
                      color: '#000000',
                    }]}
                    layout="horizontal"
                    barLabel={(v) => {
                      const sorted = sortedEmotionData;
                      const item = sorted[v.dataIndex];
                      return item ? `${item.label}: ${v.value}` : '';
                    }}
                    yAxis={[{
                      scaleType: 'band',
                      dataKey: 'label',
                      width: 130,
                    }]}
                    tooltip={{ trigger: 'item' }}
                    slots={{
                      legend: PiecewiseColorLegend,
                      barLabel: BarLabelAtBase,
                      bar: BarShadedBackground,
                      itemContent: CustomChartTooltip
                    }}
                    onItemClick={(event, d) => {
                      if (d && typeof d.dataIndex !== 'undefined') {
                        const clickedItem = sortedEmotionData[d.dataIndex];
                        handleChartClick(
                          'emotion',
                          clickedItem.id,
                          `Emotion: ${clickedItem.label}`
                        );
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      '& .MuiBarElement-root': {
                        cursor: 'pointer',
                      },
                    }}
                  />
                </Stack>

                {/* Legend with color coding */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {sortedEmotionData.slice(0, 8).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="text-xl">{getEmotionEmoji(item.emotion)}</span>
                        <div>
                          <p className="font-medium text-gray-700 capitalize">{item.emotion}</p>
                          <p className="text-xs text-gray-500">{item.value.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <EmptyChart icon={BarChart3} title="No Emotion Data" description="Emotion analysis will appear here" />
            )}
            <ChartFooter dateRange={overview?.date_range} />
          </div>

          {/* Intent Distribution Chart - Full Width */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-5 border border-gray-200 mt-4">
            <div className="flex justify-between items-center mb-3">
              <div>
                <div className="flex items-center">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900">Intent Distribution</h3>
                  <InfoTooltip text="Breakdown of what the user is trying to achieve with their query." />
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Total: {sortedIntentData.reduce((sum, item) => sum + item.value, 0)} queries • Click to filter
                </p>
              </div>
              <SortButton
                sortOrder={intentSortOrder}
                onSort={() => setIntentSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              />
            </div>
            {sortedIntentData.length > 0 ? (
              <Stack>
                <BarChart
                  height={400}
                  dataset={sortedIntentData}
                  series={[
                    {
                      id: 'turnout',
                      dataKey: 'value',
                      color: '#000000',
                    },
                  ]}
                  layout="horizontal"
                  barLabel={(v) => {
                    const item = sortedIntentData[v.dataIndex];
                    return item ? `${item.label}: ${v.value}` : '';
                  }}
                  yAxis={[
                    {
                      scaleType: 'band',
                      dataKey: 'label',
                      width: 140,
                    },
                  ]}
                  tooltip={{ trigger: 'item' }}
                  slots={{
                    legend: PiecewiseColorLegend,
                    barLabel: BarLabelAtBase,
                    bar: BarShadedBackground,
                    itemContent: CustomChartTooltip
                  }}
                  onItemClick={(event, d) => {
                    if (d && typeof d.dataIndex !== 'undefined') {
                      const clickedItem = sortedIntentData[d.dataIndex];
                      handleChartClick(
                        'intent',
                        clickedItem.id,
                        `Intent: ${clickedItem.label}`
                      );
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    '& .MuiBarElement-root': {
                      cursor: 'pointer',
                    },
                  }}
                />
              </Stack>
            ) : (
              <EmptyChart
                icon={BarChart3}
                title="No Intent Data"
                description="Intent distribution will be displayed once queries are analyzed."
              />
            )}
            <ChartFooter dateRange={queryClassification?.dateRange} />
          </div>
        </div>

        {/* Bar Chart - Full Width */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-5 border border-gray-200 mt-4 sm:mt-5">
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-4">Top Query Sources</h3>
          {barChartData.length > 0 ? (
            <div className="space-y-4">
              <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
                {barChartData.map((item, index) => {
                  const maxCount = Math.max(...barChartData.map(d => d.count));
                  const percentage = (item.count / maxCount) * 100;

                  return (
                    <div key={index} className="group">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="flex-shrink-0 text-xs font-bold text-black bg-gray-100 px-2 py-1 rounded">
                            #{index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium text-gray-700 truncate" title={item.source}>
                                {item.source}
                              </span>
                              <a
                                href={item.source}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-shrink-0 text-gray-400 hover:text-black transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                          </div>
                        </div>
                        <span className="flex-shrink-0 text-sm font-bold text-gray-900 ml-3">
                          {item.count}
                        </span>
                      </div>

                      <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden group-hover:shadow-md transition-shadow">
                        <div
                          className="absolute inset-y-0 left-0 bg-black rounded-lg transition-all duration-500 ease-out flex items-center"
                          style={{ width: `${percentage}%` }}
                        >
                          <span className="ml-3 text-xs font-semibold text-white">
                            {percentage.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <EmptyChart
              icon={BarChart3}
              title="No Source Data"
              description="Query sources will be displayed once visitors start using your platform."
            />
          )}
        </div>
      </div>



      {/* Quick Actions */}
      <div className="bg-white shadow-lg rounded-xl border border-gray-200">
        <div className="p-4 sm:p-5">
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <QuickActionCard
              title="Upload Content"
              description="Add new content to generate FAQs"
              action="Go to Knowledge Vault"
              href="/knowledge"
            />
            <QuickActionCard
              title="Create Widget"
              description="Deploy widgets on your website"
              action="Go to Widget Manager"
              href="/widgets"
            />
            <QuickActionCard
              title="View Analytics"
              description="Check your AI Query insights"
              action="Go to Analytics"
              href="/analytics"
            />
          </div>
        </div>
      </div>

      {/* Query Preview Modal */}
      <QueryPreviewModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        queries={modalQueries}
        title={modalTitle}
        filterType={modalFilterType}
        filterValue={modalFilterValue}
        loading={modalLoading}
        pagination={modalPagination}
        onPageChange={handleModalPageChange}
      />

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}

function QuickActionCard({ title, description, action, href }) {
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    navigate(href);
  };

  return (
    <div className="group border-2 border-gray-200 rounded-lg p-3 hover:border-black hover:shadow-md transition-all duration-200 cursor-pointer bg-white">
      <div onClick={handleClick}>
        <h4 className="font-semibold text-xs sm:text-sm text-gray-900 group-hover:text-black transition-colors">{title}</h4>
        <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 mb-2">{description}</p>
        <span className="inline-flex items-center text-[10px] sm:text-xs font-medium text-black group-hover:text-gray-800 group-hover:translate-x-1 transition-transform">
          {action} →
        </span>
      </div>
    </div>
  );
}

export default Overview;
